'use client';

import {
  CommandEmptySlot,
  CommandGroupSlot,
  CommandInputSlot,
  CommandItemSlot,
  CommandListSlot,
  CommandSpinnerSlot,
} from '@magicschool/spellbook/components/Command/slots';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { Command as CommandPrimitive } from 'cmdk';
import { useState } from 'react';

export type CommandProps = React.ComponentProps<typeof CommandPrimitive> &
  VariantProps<typeof commandVariants> & {
    /** Handle the loading state of the items. When `isLoading` is true, a loading spinner is displayed in the dropdown. */
    isLoading?: boolean;
    /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
    text?: {
      /** Text to display when no results are found. */
      noResultsText?: string;
    };
    /** The orientation of the description within the item. */
    descriptionOrientation?: 'horizontal' | 'vertical';
    /** Whether the command allows multiple selections. This is a stylistic choice and does not affect the underlying behavior. Control all selection logic externally. */
    multiple?: boolean;
  };

const commandVariants = cva('w-full flex flex-col overflow-hidden rounded-card-md', {
  variants: {
    /** Whether the command should have a border */
    withBorder: {
      true: 'border border-border-default',
      false: '',
    },
    withDivider: {
      true: 'divide-y divide-border-default',
      false: '',
    },
  },
});

export const CommandBase: React.FC<CommandProps> = ({
  children,
  className,
  descriptionOrientation = 'horizontal',
  disablePointerSelection = true,
  filter,
  isLoading,
  loop = false,
  multiple = false,
  text = {},
  onValueChange,
  shouldFilter = true,
  value,
  withBorder = false,
  withDivider = true,
  ...props
}) => {
  const [internalSearch, setInternalSearch] = useState('');

  const { slots, rest } = useSlots(children, {
    input: {
      Component: CommandInputSlot,
      props: {
        value: internalSearch,
        onValueChange: setInternalSearch,
        onClearSearch: () => setInternalSearch(''),
      },
    },
    list: {
      Component: CommandListSlot,
      props: {
        isLoading,
        text,
        descriptionOrientation,
        multiple,
      },
    },
  });

  return (
    <CommandPrimitive
      data-spellbook-name="command"
      className={cn(commandVariants({ withBorder, withDivider }), className)}
      onValueChange={onValueChange}
      value={value}
      disablePointerSelection={disablePointerSelection}
      loop={loop}
      filter={filter}
      shouldFilter={shouldFilter}
      {...props}
    >
      <div className="py-2.5 px-4">{slots.input}</div>
      {slots.list}
      {rest && rest.length > 0 && <div className="py-2.5 px-4">{rest}</div>}
    </CommandPrimitive>
  );
};

/**
 * A searchable command palette for keyboard-driven selection, built on cmdk. Requires manual composition within a Modal or Popover.
 *
 * ### When to use
 * - Searchable selection interfaces with custom item rendering (avatars, icons, descriptions)
 * - Large filterable lists (100+ items) where search is essential
 * - Command palettes with keyboard-first navigation (Cmd+K patterns)
 * - Grouped searchable lists (tools by category, files by folder)
 *
 * ### When NOT to use
 * - Simple dropdowns without search: use `Select` instead
 * - Form field selection with search: use `Autocomplete` instead (has built-in trigger and popover)
 * - Multiple selection with chips: use `MultiSelect` instead
 * - Action menus (edit, delete, share): use `DropdownMenu` instead
 *
 * See the [Select documentation](?path=/docs/components-select--docs) for a detailed comparison of selection components.
 *
 * ### Key differences from Autocomplete
 * - **No built-in trigger**: You must wrap Command in a `Popover` or `Modal`
 * - **Fully controlled**: You manage selection state via `value` + `onValueChange`
 * - **Custom item rendering**: Pass `children` to `Command.Item` for full control
 * - **Lower-level API**: More flexible but requires more setup
 *
 * ### API
 * - `value` / `onValueChange`: Controlled selection state (manage externally)
 * - `isLoading`: Shows spinner while fetching items
 * - `shouldFilter`: Enable/disable built-in filtering (default `true`)
 * - `filter`: Custom filter function (receives item value and search string)
 * - `loop`: Arrow keys wrap around list (default `false`)
 * - `multiple`: Enables multi-select styling (you still manage the array state)
 *
 * ### Composition (slots)
 * - `<Command.Input>`: Search input field. Defaults to `SearchInput`. Pass `overrideDefault` to customize.
 * - `<Command.List>`: Container for items. Apply `max-h-*` and `overflow-auto` for scrolling.
 * - `<Command.Item>`: Selectable item. Props: `value` (required), `label`, `description`, `onSelect`, `selected`, `keywords`.
 * - `<Command.Group>`: Groups items with a label header.
 * - `<Command.Empty>`: Shown when no items match search. Customize via `text.noResultsText`.
 * - `<Command.Spinner>`: Loading indicator (auto-shown when `isLoading={true}`).
 *
 * ### Typical usage pattern
 * ```tsx
 * <Popover>
 *   <Popover.Trigger><Button>Select...</Button></Popover.Trigger>
 *   <Popover.Content>
 *     <Command value={value} text={{ noResultsText: 'No results' }}>
 *       <Command.Input placeholder="Search..." />
 *       <Command.List className="max-h-60">
 *         {items.map(item => (
 *           <Command.Item
 *             key={item.id}
 *             value={item.id}
 *             label={item.name}
 *             onSelect={(val) => setValue(val)}
 *             selected={value === item.id}
 *           />
 *         ))}
 *       </Command.List>
 *     </Command>
 *   </Popover.Content>
 * </Popover>
 * ```
 *
 * ### Accessibility
 * - Uses combobox pattern with arrow key navigation
 * - Enter selects highlighted item, Escape closes
 * - `keywords` prop adds hidden searchable terms to items
 * - Screen readers announce item count and selection state
 *
 * @see [cmdk documentation](https://github.com/pacocoursey/cmdk)
 */
export const Command = Object.assign(CommandBase, {
  Input: CommandInputSlot,
  List: CommandListSlot,
  Item: CommandItemSlot,
  Empty: CommandEmptySlot,
  Group: CommandGroupSlot,
  Spinner: CommandSpinnerSlot,
});
